import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Lightbulb, TrendingUp, TrendingDown, Info } from 'lucide-react';
import type { TechnicianPensieveStats } from '@/hooks/useTechnicianPensieveStats';
import { derivePensieveCoaching } from '@/utils/pensieveCoaching';
import { PassportProvenanceBadge } from './PassportProvenanceBadge';

interface PassportPensieveCoachingProps {
  stats: TechnicianPensieveStats | undefined;
  isLoading: boolean;
}

export function PassportPensieveCoaching({
  stats,
  isLoading,
}: PassportPensieveCoachingProps) {
  if (isLoading) {
    return (
      <Card className="bg-muted/40">
        <CardHeader className="flex flex-row items-center gap-2 pb-2">
          <Lightbulb className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">
            Coaching & Future Insights
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
          <div className="h-3 w-2/3 rounded bg-muted animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  if (!stats || !stats.hasData) {
    return (
      <Card className="bg-muted/40">
        <CardHeader className="flex flex-row items-center gap-2 pb-2">
          <Lightbulb className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">
            Coaching & Future Insights
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="h-3 w-3 mt-0.5 shrink-0" />
          <span>
            OptiTime is still learning your patterns. As more repeat jobs are
            completed on the same vehicles, OptiBay will surface coaching
            insights here.
          </span>
        </CardContent>
      </Card>
    );
  }

  const insights = derivePensieveCoaching(stats);

  if (!insights.length) {
    return (
      <Card className="bg-muted/40">
        <CardHeader className="flex flex-row items-center gap-2 pb-2">
          <Lightbulb className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">
            Coaching & Future Insights
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="h-3 w-3 mt-0.5 shrink-0" />
          <span>
            OptiBay has data for your completed jobs, but hasn&apos;t detected
            strong patterns yet. Keep going — OptiTime will highlight trends as
            they emerge.
          </span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-muted/40">
      <CardHeader className="flex flex-row items-center gap-2 pb-2">
        <Lightbulb className="h-4 w-4 text-muted-foreground" />
        <CardTitle className="text-sm font-medium">
          Coaching & Future Insights
        <PassportProvenanceBadge factGroupId="pensieve-learning" className="ml-2" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {insights.map((insight) => {
          const icon =
            insight.tone === 'strength' ? (
              <TrendingUp className="h-3 w-3" />
            ) : insight.tone === 'growth' ? (
              <TrendingDown className="h-3 w-3" />
            ) : (
              <Info className="h-3 w-3" />
            );

          const badgeVariant =
            insight.tone === 'strength'
              ? 'default'
              : insight.tone === 'growth'
              ? 'destructive'
              : 'outline';

          return (
            <div key={insight.id} className="flex flex-col gap-1 text-xs">
              <div className="flex items-center gap-2">
                <Badge variant={badgeVariant} className="gap-1 px-2 py-0">
                  {icon}
                  <span className="text-[10px] uppercase tracking-wide">
                    {insight.tone === 'strength'
                      ? 'Strength'
                      : insight.tone === 'growth'
                      ? 'Growth Edge'
                      : 'Insight'}
                  </span>
                </Badge>
                <span className="font-medium">{insight.summary}</span>
              </div>
              {insight.detail && (
                <p className="pl-1 text-muted-foreground">{insight.detail}</p>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
